import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useBranding } from "../../branding/useBranding";
import { userAtom, biometricAuthAtom, darkModeAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { Link } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { getAPIClient } from "../../apiClient";
import logout from "../../utils/logout";
import { BiometricAuth, BiometryType } from "@aparajita/capacitor-biometric-auth";
import { getBiometricLabel } from "../../utils/biometrics";
import { vibrateDevice } from "../../utils/capacitor";
import { ImageIcon } from "../ImageIcon";
import { Modal } from "../modals/Modal";
import { WHITELABEL_KEY } from "../../globals";

export function Drawer({ id }: { id: string }) {
  const [user] = useAtom(userAtom);
  const branding = useBranding();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isWeb = Capacitor.getPlatform() === "web";

  // Logout confirmation modal state
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // High contrast state
  const [darkMode, setDarkMode] = useAtom(darkModeAtom);

  // Biometrics state
  const [biometricAuth, setBiometricAuth] = useAtom(biometricAuthAtom);
  const [biometryType, setBiometryType] = useState<BiometryType | null>(null);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      BiometricAuth.checkBiometry().then((value) => {
        if (value.isAvailable) {
          setBiometryType(value.biometryType);
        }
      });
    }
  }, []);

  const toggleDarkMode = async () => {
    if (!darkMode) await vibrateDevice();
    setDarkMode(!darkMode);
  };

  const toggleBiometricAuth = async () => {
    if (biometryType && !biometricAuth) {
      try {
        await BiometricAuth.authenticate();
        await vibrateDevice();
        setBiometricAuth(true);
      } catch (error) {
        setBiometricAuth(false);
      }
    } else {
      setBiometricAuth(false);
    }
  };

  const handleLogoutClick = async () => {
    if (isWeb) {
      setShowLogoutModal(true);
    } else {
      const { value } = await Dialog.confirm({
        title: t("menu.sign_out_confirmation_title"),
        message: t("menu.sign_out_confirmation_message"),
        okButtonTitle: t("generic.ok"),
        cancelButtonTitle: t("generic.cancel"),
      });
      if (value) {
        await logout(getAPIClient(), getAuth());
        navigate("/");
      }
    }
  };

  const showBiometrics =
    Capacitor.isNativePlatform() && biometryType !== null && biometryType !== BiometryType.none;

  return (
    <>
      {showLogoutModal && (
        <Modal
          id="logout-confirmation-modal"
          open={showLogoutModal}
          title={t("menu.sign_out_confirmation_title")}
          onCloseModal={() => setShowLogoutModal(false)}
          hasClose={true}
          actionButtons={
            <>
              <button className="btn info" onClick={() => setShowLogoutModal(false)}>
                {t("generic.cancel")}
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  await logout(getAPIClient(), getAuth());
                  navigate("/");
                }}
              >
                {t("menu.sign_out")}
              </button>
            </>
          }
        >
          <p>{t("menu.sign_out_confirmation_message")}</p>
        </Modal>
      )}
      <div className="drawer-side z-50 h-screen">
        <label htmlFor={id} aria-label="close sidebar" className="drawer-overlay"></label>
        <div className="flex flex-col menu bg-base-100 text-base-content min-h-full p-0 text-xl font-bold  max-w-[100vw] pt-[env(safe-area-inset-top)]">
          <div className="py-8 pr-8 pl-4 flex flex-col w-full gap-4">
            <img className="h-12 mx-auto" src={`/${branding.logo}`} alt="Docket logo" />
            <div className="flex flex-col break-words">
              <p className="text-lg text-center font-bold">
                {`${user?.first_name} ${user?.last_name}`}
              </p>
              <p className="text-sm text-center">{user?.email}</p>
            </div>
            <li
              className="gap-2"
              onClick={() => {
                document.getElementById(id)?.click();
              }}
            >
              <Link to="/home/search" draggable="false">
                <ImageIcon
                  src="/assets/images/Icon_Search.png"
                  className="text-primary w-10 h-10"
                />
                <span>{t("immunizations.tab_bar_search_title")}</span>
              </Link>
              <Link to="/home/records" draggable="false">
                <ImageIcon
                  src="/assets/images/Icon_Immunizations.png"
                  className="text-primary w-10 h-10"
                />
                <span>{t("menu.immunizations")}</span>
              </Link>
              <Link to="/home/settings" draggable="false">
                <ImageIcon
                  src="/assets/images/Icon_Settings.png"
                  className="text-primary w-10 h-10"
                />
                {t("menu.settings")}
              </Link>
              <button onClick={handleLogoutClick}>
                <ImageIcon
                  src="/assets/images/Icon_Log_Out.png"
                  className="text-primary w-10 h-10"
                />
                {t("menu.sign_out")}
              </button>
              <Link to="/home/discover" draggable="false">
                <ImageIcon
                  src="/assets/images/Icon_Partners.png"
                  className="text-primary w-10 h-10"
                />
                {t("menu.discover")}
              </Link>
              <Link
                to="/home/records-policy"
                state={{ back: true, title: "FAQs" }}
                draggable="false"
              >
                <ImageIcon src="/assets/images/Icon_FAQs.png" className="text-primary w-10 h-10" />
                FAQs
              </Link>
            </li>

            {/* Toggles Section */}
            <div className="border-t-2 border-primary pt-4 mt-2 gap-4">
              {WHITELABEL_KEY !== "nj" && (
                <label className="flex items-center justify-between cursor-pointer py-2">
                  <span className="text-base font-semibold">Dark Mode</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-lg toggle-primary"
                    checked={darkMode}
                    onChange={toggleDarkMode}
                    data-testid="drawer-high-contrast-toggle"
                  />
                </label>
              )}

              {showBiometrics && (
                <label className="flex items-center justify-between cursor-pointer py-2">
                  <span className="text-base font-semibold">
                    {getBiometricLabel(biometryType!)}
                  </span>
                  <input
                    type="checkbox"
                    className="toggle toggle-lg toggle-primary"
                    checked={biometricAuth}
                    onChange={toggleBiometricAuth}
                    data-testid="drawer-biometric-toggle"
                  />
                </label>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
